// src/components/JobFilters.tsx
import React, { useState, useEffect, useRef } from 'react';
import { MapContext } from '../contexts/MapContext';
import { useAuth } from '../contexts/AuthContext';
import { useNumberInput } from '../hooks/useNumberInput';
import JobList from './JobList';
import JobMarketInsights from './JobMarketInsights';
import CompareAnalysisModal from './CompareAnalysisModal';
import './JobFilters.css';
import './JobFilters.mobile.css';
import './UnifiedSidebar.css';

type FilterType = 'country' | 'industry' | 'jobType' | 'position' | 'search' | 'salary';

interface JobFiltersProps {
  onFilterChange: (filters: any) => void;
  filters: any;
  darkMode: boolean;
  jobs?: any[];
  selectedLocation?: {
    city: string;
    country: string;
    costOfLiving: number;
    averageSalary: number;
  } | null;
  onLoginRequired?: (action: string) => void;
  onJobClick?: (job: any) => void;
}

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
}

// MultiSelect component for reusable filter dropdowns
const MultiSelect: React.FC<MultiSelectProps> = ({ 
  label, 
  options, 
  selected, 
  onChange, 
  placeholder = "Select options",
  searchPlaceholder = "Search..."
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Filter options based on search term
  const filteredOptions = options.filter((option: string) => 
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Toggle option selection
  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((item: string) => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };
  
  return (
    <div className="filter-group">
      <label>{label}</label>
      <div className="multi-select-container" ref={dropdownRef}>
        {/* Header/trigger for the dropdown */}
        <div 
          className="multi-select-header"
          onClick={() => setIsOpen(!isOpen)}
        >
          {selected.length > 0 ? `${selected.length} selected` : placeholder}
          <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
        </div>
        
        {/* Selected options as tags */}
        {selected.length > 0 && (
          <div className="selected-tags">
            {selected.map((option: string) => (
              <div key={option} className="selected-tag">
                {option}
                <button 
                  className="tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(selected.filter((item: string) => item !== option));
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            {selected.length > 0 && (
              <button 
                className="clear-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}
        
        {/* Dropdown content */}
        {isOpen && (
          <div className="multi-select-dropdown">
            <input
              type="text"
              className="dropdown-search"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            
            <div className="options-list">
              {filteredOptions.map((option: string) => (
                <div 
                  key={option}
                  className={`option-item ${selected.includes(option) ? 'selected' : ''}`}
                  onClick={() => toggleOption(option)}
                >
                  {option}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className="no-options">No matching options found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const JobFilters: React.FC<JobFiltersProps> = ({ onFilterChange, filters, darkMode, jobs = [], selectedLocation, onLoginRequired, onJobClick }) => {
  // State for collapsible sections
  const [isFormCollapsed, setIsFormCollapsed] = useState<boolean>(false);
  const [isStatsCollapsed, setIsStatsCollapsed] = useState<boolean>(false);
  const [showCompareModal, setShowCompareModal] = useState<boolean>(false);
  
  // Track if component is mounted to prevent initial effect run
  const isMounted = useRef(false);
  
  // Refs for scroll detection
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);
  const scrollDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const isStatsCollapsedRef = useRef(isStatsCollapsed);
  const isFormCollapsedRef = useRef(isFormCollapsed);
  
  // Keep refs in sync with state
  useEffect(() => {
    isStatsCollapsedRef.current = isStatsCollapsed;
  }, [isStatsCollapsed]);
  
  useEffect(() => {
    isFormCollapsedRef.current = isFormCollapsed;
  }, [isFormCollapsed]);
  
  // Auth context
  const { isAuthenticated } = useAuth();
  
  // State for filters
  const [searchTerm, setSearchTerm] = useState<string>(filters.search || '');
  const [selectedCountries, setSelectedCountries] = useState<string[]>(filters.countries || []);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>(filters.industries || []);
  const [selectedJobTypes, setSelectedJobTypes] = useState<string[]>(filters.jobTypes || []);
  const [selectedPositions, setSelectedPositions] = useState<string[]>(filters.positions || []);
  const [salaryRange, setSalaryRange] = useState<{min: number, max: number}>({
    min: filters.salary ? filters.salary[0] : 0,
    max: filters.salary ? filters.salary[1] : 200000
  });
  
  const minSalaryInput = useNumberInput({
    initialValue: salaryRange.min,
    min: 0,
    max: 1000000,
    step: 1000,
    allowEmpty: true,
    onValidChange: (value) => setSalaryRange(prev => ({ ...prev, min: value }))
  });
  
  const maxSalaryInput = useNumberInput({
    initialValue: salaryRange.max,
    min: 0,
    max: 1000000,
    step: 1000,
    allowEmpty: true,
    onValidChange: (value) => setSalaryRange(prev => ({ ...prev, max: value }))
  });
  
  // Sample data for our filters
  const countries: string[] = ["USA", "UK", "Germany", "France", "Canada", "Australia", "Japan", "India", "China"];
  const industries: string[] = ["Technology", "Finance", "Healthcare", "Education", "Marketing", "Data Science", "Engineering", "Manufacturing"];
  const jobTypes: string[] = ["Full-time", "Part-time", "Contract", "Freelance", "Internship", "Remote"];
  const jobPositions: string[] = ["Software Engineer", "Product Manager", "Data Scientist", "UX Designer", "Marketing Specialist"];
  
  // Apply filters to the main application
  // Apply filters automatically when any filter changes
  useEffect(() => {
    // Skip the first render to prevent infinite loop
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const timeoutId = setTimeout(() => {
      onFilterChange({
        search: searchTerm,
        countries: selectedCountries,
        industries: selectedIndustries,
        jobTypes: selectedJobTypes,
        positions: selectedPositions,
        salary: [salaryRange.min, salaryRange.max]
      });
    }, 300); // Debounce for 300ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCountries, selectedIndustries, selectedJobTypes, selectedPositions, salaryRange.min, salaryRange.max]); // Note: removed onFilterChange from deps
  
  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCountries([]);
    setSelectedIndustries([]);
    setSelectedJobTypes([]);
    setSelectedPositions([]);
    setSalaryRange({ min: 0, max: 200000 });
    
    onFilterChange({
      search: '',
      countries: [],
      industries: [],
      jobTypes: [],
      positions: [],
      salary: [0, 200000]
    });
  };
  
  // Check if there are any active filters
  const hasActiveFilters = (): boolean => {
    return selectedCountries.length > 0 || 
      selectedIndustries.length > 0 || 
      selectedJobTypes.length > 0 || 
      selectedPositions.length > 0 || 
      searchTerm !== '' || 
      salaryRange.min !== 0 || 
      salaryRange.max !== 200000;
  };
  
  // Toggle form collapse
  const toggleFormCollapse = () => {
    setIsFormCollapsed(!isFormCollapsed);
  };

  const removeFilter = (type: FilterType, value: string): void => {
    // Create a copy of current filters
    const updatedFilters = { ...filters };
    
    // Update the specific filter type
    switch(type) {
      case 'country':
        updatedFilters.countries = filters.countries.filter((c: string) => c !== value);
        break;
      case 'industry':
        updatedFilters.industries = filters.industries.filter((i: string) => i !== value);
        break;
      case 'jobType':
        updatedFilters.jobTypes = filters.jobTypes.filter((t: string) => t !== value);
        break;
      case 'position':
        updatedFilters.positions = filters.positions.filter((p: string) => p !== value);
        break;
      case 'search':
        updatedFilters.search = '';
        break;
      case 'salary':
        updatedFilters.salary = [0, 200000];
        break;
    }
    
    // Immediately update parent component to refresh the job list
    onFilterChange(updatedFilters);
  }

  // Auto-collapse form after applying filters
  useEffect(() => {
    // When there are no active filters, always show the filter form
    if (!hasActiveFilters()) {
      setIsFormCollapsed(false);
    }
  }, [selectedCountries, selectedIndustries, selectedJobTypes, selectedPositions, searchTerm, salaryRange]);

  // Auto-collapse insights and filters on scroll down
  useEffect(() => {
    const scrollElements: HTMLElement[] = [];
    let sidebarLastScroll = 0;
    let jobListLastScroll = 0;
    
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const scrollTop = target.scrollTop;
      const isSidebar = target.classList.contains('unified-sidebar-content');
      
      // Get the appropriate last scroll value
      const lastScroll = isSidebar ? sidebarLastScroll : jobListLastScroll;
      
      // Very low threshold for sidebar since it has limited scroll range
      const scrollThreshold = isSidebar ? 5 : 30;
      
      // Only proceed if we've scrolled down more than threshold
      if (scrollTop > scrollThreshold && scrollTop > lastScroll) {
        // Collapse insights section
        if (!isStatsCollapsedRef.current) {
          setIsStatsCollapsed(true);
          isStatsCollapsedRef.current = true;
        }
        
        // ONLY collapse filters if there are active filters
        // Never collapse if no filters are active
        if (hasActiveFilters() && !isFormCollapsedRef.current) {
          setIsFormCollapsed(true);
          isFormCollapsedRef.current = true;
        }
      }
      
      // Update the appropriate scroll position
      if (isSidebar) {
        sidebarLastScroll = scrollTop;
      } else {
        jobListLastScroll = scrollTop;
      }
    };

    // Set up the scroll listeners
    const timer = setTimeout(() => {
      // Try the sidebar content first
      const sidebarElement = document.querySelector('.filters-container .unified-sidebar-content');
      if (sidebarElement) {
        sidebarElement.addEventListener('scroll', handleScroll, { passive: true });
        scrollElements.push(sidebarElement as HTMLElement);
      }
      
      // Also attach to the job list
      const jobListElement = document.querySelector('.filters-container .unified-content-section.scrollable');
      if (jobListElement) {
        jobListElement.addEventListener('scroll', handleScroll, { passive: true });
        scrollElements.push(jobListElement as HTMLElement);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      scrollElements.forEach(element => {
        element.removeEventListener('scroll', handleScroll);
      });
    };
  }, [isStatsCollapsed, isFormCollapsed]);
  
  return (
    <div className="unified-sidebar filters-container">
      <div className="sidebar-header">
        <h2><span className="material-icons">work_outline</span> Job Market Filters</h2>
        <p>Find your perfect opportunity</p>
      </div>
      <div className="unified-sidebar-content">
        <div className="unified-content-section filters-header">
          <div className="active-filters-count">
            {hasActiveFilters() ? `${
              selectedCountries.length + 
              selectedIndustries.length + 
              selectedJobTypes.length + 
              selectedPositions.length + 
              (searchTerm ? 1 : 0) + 
              ((salaryRange.min > 0 || salaryRange.max < 200000) ? 1 : 0)
            } active filters` : 'No filters applied'}
          </div>
          {/* Toggle button to expand/collapse the form */}
          {hasActiveFilters() && (
            <button 
              className="toggle-form-btn"
              onClick={toggleFormCollapse}
              aria-label={isFormCollapsed ? "Expand filters" : "Collapse filters"}
            >
              {isFormCollapsed ? "+" : "−"}
            </button>
          )}
        </div>
        
        {/* Selected Filters - Always visible */}
        {hasActiveFilters() && (
          <div className="unified-content-section selected-filters-container">
          <h3 className="selected-filters-title">Selected Filters:</h3>
          <div className="selected-filters-list">
            {selectedCountries.map((country: string) => (
              <div key={`country-${country}`} className="filter-tag country-tag">
                {country}
                <button 
                  className="filter-tag-remove"
                  onClick={() => setSelectedCountries(
                    selectedCountries.filter(item => item !== country)
                  )}
                >
                  ×
                </button>
              </div>
            ))}
            
            {selectedIndustries.map((industry: string) => (
              <div key={`industry-${industry}`} className="filter-tag industry-tag">
                {industry}
                <button 
                  className="filter-tag-remove"
                  onClick={() => setSelectedIndustries(
                    selectedIndustries.filter(item => item !== industry)
                  )}
                >
                  ×
                </button>
              </div>
            ))}
            
            {selectedJobTypes.map((jobType: string) => (
              <div key={`type-${jobType}`} className="filter-tag job-type-tag">
                {jobType}
                <button 
                  className="filter-tag-remove"
                  onClick={() => setSelectedJobTypes(
                    selectedJobTypes.filter(item => item !== jobType)
                  )}
                >
                  ×
                </button>
              </div>
            ))}
            
            {selectedPositions.map((position: string) => (
              <div key={`position-${position}`} className="filter-tag position-tag">
                {position}
                <button 
                  className="filter-tag-remove"
                  onClick={() => setSelectedPositions(
                    selectedPositions.filter(item => item !== position)
                  )}
                >
                  ×
                </button>
              </div>
            ))}
            
            {/* Show salary range as a filter tag if modified */}
            {(salaryRange.min > 0 || salaryRange.max < 200000) && (
              <div className="filter-tag salary-tag">
                ${salaryRange.min.toLocaleString()} - ${salaryRange.max.toLocaleString()}
                <button 
                  className="filter-tag-remove"
                  onClick={() => setSalaryRange({ min: 0, max: 200000 })}
                >
                  ×
                </button>
              </div>
            )}
            
            {/* Show search term as a filter tag if present */}
            {searchTerm && (
              <div className="filter-tag search-tag">
                "{searchTerm}"
                <button 
                  className="filter-tag-remove"
                  onClick={() => setSearchTerm('')}
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Collapsible Form Section */}
      <div className={`unified-content-section filter-form ${isFormCollapsed ? 'collapsed' : ''}`}>
        {/* Search input */}
        <div className="search-container">
          <input 
            type="text"
            className="search-input"
            placeholder="Search jobs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* Countries multi-select */}
        <MultiSelect 
          label="Countries"
          options={countries}
          selected={selectedCountries}
          onChange={setSelectedCountries}
          placeholder="All Countries"
          searchPlaceholder="Search countries..."
        />
        
        {/* Industries multi-select */}
        <MultiSelect 
          label="Industries"
          options={industries}
          selected={selectedIndustries}
          onChange={setSelectedIndustries}
          placeholder="All Industries"
          searchPlaceholder="Search industries..."
        />
        
        {/* Job Types multi-select */}
        <MultiSelect 
          label="Job Types"
          options={jobTypes}
          selected={selectedJobTypes}
          onChange={setSelectedJobTypes}
          placeholder="All Job Types"
          searchPlaceholder="Search job types..."
        />
        
        {/* Job Positions multi-select */}
        <MultiSelect 
          label="Positions"
          options={jobPositions}
          selected={selectedPositions}
          onChange={setSelectedPositions}
          placeholder="All Positions"
          searchPlaceholder="Search positions..."
        />
        
        {/* Salary Range */}
        <div className="filter-group">
          <label>Salary Range</label>
          <div className="salary-range-container">
            <div className="salary-input-wrapper">
              <div className={`salary-input-group ${!minSalaryInput.isValid ? 'error' : ''}`}>
                <label className="salary-icon">
                  <span>$</span>
                </label>
                <input 
                  type="number"
                  value={minSalaryInput.value}
                  onChange={minSalaryInput.onChange}
                  onBlur={minSalaryInput.onBlur}
                  min="0"
                  step="1000"
                  placeholder="Min"
                />
              </div>
              {minSalaryInput.error && (
                <span className="input-error">{minSalaryInput.error}</span>
              )}
            </div>
            <span className="salary-separator">-</span>
            <div className="salary-input-wrapper">
              <div className={`salary-input-group ${!maxSalaryInput.isValid ? 'error' : ''}`}>
                <label className="salary-icon">
                  <span>$</span>
                </label>
                <input 
                  type="number"
                  value={maxSalaryInput.value}
                  onChange={maxSalaryInput.onChange}
                  onBlur={maxSalaryInput.onBlur}
                  min="0"
                  step="1000"
                  placeholder="Max"
                />
              </div>
              {maxSalaryInput.error && (
                <span className="input-error">{maxSalaryInput.error}</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Reset button */}
        <div className="filter-actions">
          <button 
            className="reset-filters-btn"
            onClick={resetFilters}
          >
            Reset Filters
          </button>
        </div>
      </div>
      
      {/* Job Market Stats - Collapsible */}
      <div className="unified-content-section">
        <div className="section-header">
          <h3>Job Market Stats</h3>
          <button 
            className="toggle-collapse-btn"
            onClick={() => setIsStatsCollapsed(!isStatsCollapsed)}
            aria-label="View detailed insights"
            title="View detailed market insights"
          >
            <span className="material-icons">
              insights
            </span>
          </button>
        </div>
        {!isStatsCollapsed && (
          <div className="stats-content">
            <div className="stat-item">
              <span className="stat-label">Total Jobs</span>
              <span className="stat-value">{jobs.length.toLocaleString()}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Avg. Salary</span>
              <span className="stat-value">
                ${jobs.length > 0 
                  ? Math.round(jobs.reduce((sum, job) => sum + (job.salary || 0), 0) / jobs.length).toLocaleString()
                  : '0'}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Remote Jobs</span>
              <span className="stat-value">
                {jobs.length > 0 
                  ? Math.round((jobs.filter(job => job.remote).length / jobs.length) * 100) + '%'
                  : '0%'}
              </span>
            </div>
            
            {/* Top Industry */}
            {(() => {
              const topIndustry = jobs.reduce((acc, job) => {
                const industry = job.industry || 'Other';
                acc[industry] = (acc[industry] || 0) + 1;
                return acc;
              }, {} as Record<string, number>);
              
              const sortedIndustries = Object.entries(topIndustry)
                .sort(([, a], [, b]) => (b as number) - (a as number));
              const [topInd, count] = sortedIndustries.length > 0 ? sortedIndustries[0] : ['N/A', 0];
              
              return (
                <div 
                  className="stat-item clickable"
                  onClick={() => {
                    if (topInd !== 'N/A') {
                      setSelectedIndustries([topInd]);
                      setIsFormCollapsed(false);
                    }
                  }}
                  style={{ cursor: topInd !== 'N/A' ? 'pointer' : 'default' }}
                >
                  <span className="stat-label">Top Industry</span>
                  <span className="stat-value">{topInd}</span>
                </div>
              );
            })()}
            
            {/* Market Insights Component */}
            <div style={{ marginTop: '16px' }}>
              <JobMarketInsights 
                jobs={jobs} 
                darkMode={darkMode} 
                selectedLocation={selectedLocation}
                onIndustryClick={(industry) => {
                  setSelectedIndustries([industry]);
                  setIsFormCollapsed(false);
                }}
                onEmploymentTypeClick={(type) => {
                  // Map employment type to job type format
                  const jobType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
                  setSelectedJobTypes([jobType]);
                  setIsFormCollapsed(false);
                }}
                onCountryClick={(country) => {
                  setSelectedCountries([country]);
                  setIsFormCollapsed(false);
                }}
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Job List Results */}
      <div className="unified-content-section scrollable" style={{ flex: 1 }}>
        <JobList 
          jobs={jobs} 
          darkMode={darkMode}
          selectedLocation={selectedLocation}
          onJobClick={onJobClick}
        />
      </div>
      
      {/* Footer with upload resume button */}
      <div className="sidebar-footer">
        <button 
          className="upload-btn"
          onClick={() => {
            if (!isAuthenticated && onLoginRequired) {
              onLoginRequired('upload-resume');
            } else {
              setShowCompareModal(true);
            }
          }}
        >
          Upload Resume
        </button>
        <div className="recommendations-text">Get personalized job recommendations</div>
      </div>
      </div>
      
      {/* Resume Upload Modal */}
      {showCompareModal && (
        <CompareAnalysisModal
          isOpen={showCompareModal}
          onClose={() => setShowCompareModal(false)}
          darkMode={darkMode}
          onLoginRequired={onLoginRequired}
          mode="upload-resume"
        />
      )}
    </div>
  );
};

export default JobFilters;